import React, { useEffect, useRef, useState } from "react";

/* ─── Network canvas background ─────────────────────────────────── */
function useNetworkCanvas(ref) {
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const root = canvas.parentElement;
    let W = (canvas.width = root.offsetWidth);
    let H = (canvas.height = root.offsetHeight);
    let raf;

    const nodes = Array.from({ length: 26 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      r: 1.5 + Math.random() * 2,
      o: 0.08 + Math.random() * 0.18,
    }));

    function tick() {
      raf = requestAnimationFrame(tick);
      ctx.clearRect(0, 0, W, H);
      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;
      });
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const d = Math.hypot(
            nodes[i].x - nodes[j].x,
            nodes[i].y - nodes[j].y
          );
          if (d < 140) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(79,70,229,${(1 - d / 140) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      nodes.forEach((n) => {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(79,70,229,${n.o})`;
        ctx.fill();
      });
    }

    tick();
    return () => cancelAnimationFrame(raf);
  }, [ref]);
}

/* ─── Inline styles (injected once into <head>) ──────────────────── */
const STYLES = `
  @keyframes th-hex-pulse {
    0%, 100% { transform: scale(1); }
    50%       { transform: scale(1.06); }
  }
  @keyframes th-orbit-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes th-dot-pulse {
    0%, 80%, 100% { transform: scale(0.45); opacity: 0.2; }
    40%           { transform: scale(1);    opacity: 1; }
  }
  @keyframes th-loader-fadeout {
    from { opacity: 1; }
    to   { opacity: 0; }
  }

  .th-loader-root {
    position: fixed;
    inset: 0;
    background: #f8f7ff;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    overflow: hidden;
  }
  .th-loader-root.th-fading {
    animation: th-loader-fadeout 0.3s ease forwards;
    pointer-events: none;
  }

  .th-loader-canvas-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  .th-loader-center {
    position: relative;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 26px;
  }

  .th-loader-hex-orbit {
    position: relative;
    width: 110px;
    height: 110px;
  }
  .th-loader-hex-svg {
    position: absolute;
    inset: 0;
    width: 110px;
    height: 110px;
    animation: th-hex-pulse 2.4s ease-in-out infinite;
  }
  .th-loader-orbit-ring {
    position: absolute;
    inset: -18px;
    width: 146px;
    height: 146px;
    animation: th-orbit-spin 6s linear infinite;
  }
  .th-loader-orbit-ring2 {
    position: absolute;
    inset: -34px;
    width: 178px;
    height: 178px;
    animation: th-orbit-spin 10s linear infinite reverse;
  }

  .th-loader-prog-zone {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    width: 240px;
  }
  .th-loader-prog-track {
    width: 100%;
    height: 2px;
    background: rgba(79,70,229,.1);
    border-radius: 99px;
    overflow: hidden;
  }
  .th-loader-prog-fill {
    height: 100%;
    background: #263012;
    border-radius: 99px;
    transition: width 0.1s linear;
  }

  .th-loader-dots { display: flex; gap: 6px; }
  .th-loader-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #263012;
    animation: th-dot-pulse 1.4s ease-in-out infinite;
  }
  .th-loader-dot:nth-child(2) { animation-delay: 0.2s; }
  .th-loader-dot:nth-child(3) { animation-delay: 0.4s; }
`;

/* ─── Main component ─────────────────────────────────────────────── */

/**
 * TalentHiveLoader
 *
 * Props
 * ─────
 * duration   {number}    ms to reach 100% (default: 350)
 * onComplete {function}  called after progress + fade-out finish
 *
 * Parent usage
 * ────────────
 * const [loading, setLoading] = useState(true);
 *
 * {loading && (
 *   <TalentHiveLoader
 *     duration={350}
 *     onComplete={() => setLoading(false)}
 *   />
 * )}
 */
export default function TalentHiveLoader({ duration = 2500, onComplete }) {
  const canvasRef = useRef(null);
  const [prog, setProg]     = useState(0);
  const [fading, setFading] = useState(false);

  useNetworkCanvas(canvasRef);

  /* Inject CSS once */
  useEffect(() => {
    if (!document.getElementById("th-loader-styles")) {
      const tag = document.createElement("style");
      tag.id = "th-loader-styles";
      tag.textContent = STYLES;
      document.head.appendChild(tag);
    }
  }, []);

  /* Time-based progress → completes in exactly `duration` ms */
useEffect(() => {
  const start = performance.now();
  let raf;

  function update() {
    const elapsed = performance.now() - start;
    let progress = elapsed / duration;

    if (progress > 1) progress = 1;

    // smooth ease progress
    const eased = 1 - Math.pow(1 - progress, 3);

    setProg(eased * 100);

    if (progress < 1) {
      raf = requestAnimationFrame(update);
    } else {
      setTimeout(() => {
        setFading(true);

        setTimeout(() => {
          if (onComplete) onComplete();
        }, 300);
      }, 120);
    }
  }

  raf = requestAnimationFrame(update);
  return () => cancelAnimationFrame(raf);
}, [duration, onComplete]);

  return (
    <div className={`th-loader-root${fading ? " th-fading" : ""}`}>
      {/* Animated node network */}
      <canvas ref={canvasRef} className="th-loader-canvas-bg" />

      <div className="th-loader-center">
        {/* Hex logo + orbit rings */}
        <div className="th-loader-hex-orbit">
          <svg className="th-loader-orbit-ring2" viewBox="0 0 178 178" fill="none">
            <circle
              cx="89" cy="89" r="82"
              stroke="rgba(79,70,229,.08)"
              strokeWidth="1"
              strokeDasharray="3 7"
            />
            <circle cx="89"  cy="7"  r="4" fill="rgba(79,70,229,.25)" />
            <circle cx="171" cy="89" r="3" fill="rgba(79,70,229,.15)" />
            <circle cx="7"   cy="89" r="3" fill="rgba(79,70,229,.15)" />
          </svg>

          <svg className="th-loader-orbit-ring" viewBox="0 0 146 146" fill="none">
            <circle
              cx="73" cy="73" r="67"
              stroke="rgba(79,70,229,.14)"
              strokeWidth="0.5"
              strokeDasharray="2 5"
            />
            <circle cx="73"  cy="6"   r="5"   fill="#263012" />
            <circle cx="140" cy="73"  r="3.5" fill="rgba(79,70,229,.45)" />
            <circle cx="73"  cy="140" r="3.5" fill="rgba(79,70,229,.45)" />
            <circle cx="6"   cy="73"  r="3"   fill="rgba(79,70,229,.3)" />
          </svg>

          <svg className="th-loader-hex-svg" viewBox="0 0 110 110" fill="none">
            <polygon
              points="55,4 99,28.5 99,81.5 55,106 11,81.5 11,28.5"
              fill="rgba(79,70,229,.04)"
              stroke="rgba(79,70,229,.15)"
              strokeWidth="1"
            />
            <polygon
              points="55,16 88,34.5 88,75.5 55,94 22,75.5 22,34.5"
              fill="rgba(79,70,229,.07)"
              stroke="rgba(79,70,229,.25)"
              strokeWidth="1"
            />
            <polygon
              points="55,28 77,40.5 77,65.5 55,78 33,65.5 33,40.5"
              fill="rgba(79,70,229,.14)"
              stroke="#263012"
              strokeWidth="1.5"
            />
            <text
              x="55" y="60"
              textAnchor="middle"
              fontSize="16"
              fontWeight="500"
              fill="#263012"
              fontFamily="sans-serif"
            >
              TH
            </text>
          </svg>
        </div>

        {/* Progress bar + bouncing dots */}
        <div className="th-loader-prog-zone">
          <div className="th-loader-prog-track">
            <div
              className="th-loader-prog-fill"
              style={{ width: `${prog.toFixed(1)}%` }}
            />
          </div>
          <div className="th-loader-dots">
            <span className="th-loader-dot" />
            <span className="th-loader-dot" />
            <span className="th-loader-dot" />
          </div>
        </div>
      </div>
    </div>
  );
}